import { AlertTriangle, CalendarDays, Clock, Plus, Pencil, Trash2, Camera, TrendingDown, TrendingUp } from "lucide-react";
import { useMemo, useState } from "react";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { LocacoesStatCard } from "./LocacoesStatCard";
import {
  registroHorimetroFotoUrl,
  type MaquinarioDto,
  type PeriodRange,
  type RegistroHorimetroDto,
  type RegistroHorimetroFotoDto,
} from "@/services/locacoes";
import { formatDate } from "@/utils/format";
import {
  computeMaquinaHorasNoPeriodo,
  countRegistrosHorimetroForaDoPeriodo,
  filterHorimetroDiaRowsByPeriod,
  formatHoras,
  formatPeriodLabel,
  groupRegistrosPorDia,
  locacoesTabContentClass,
  locacoesTableHeadClass,
  locacoesTableWrapClass,
  resolveMaquinaNome,
  type HorimetroDiaRow,
} from "./locacoesUtils";

type FotoGaleriaItem = {
  key: string;
  registroId: string;
  foto: RegistroHorimetroFotoDto;
  origem: "Início" | "Final";
};

function collectFotosDaLinha(row: HorimetroDiaRow): FotoGaleriaItem[] {
  const items: FotoGaleriaItem[] = [];
  for (const foto of row.inicio?.fotos ?? []) {
    items.push({
      key: `inicio-${foto.id}`,
      registroId: row.inicio!.id,
      foto,
      origem: "Início",
    });
  }
  for (const foto of row.final?.fotos ?? []) {
    items.push({
      key: `final-${foto.id}`,
      registroId: row.final!.id,
      foto,
      origem: "Final",
    });
  }
  return items;
}

interface Props {
  registros: RegistroHorimetroDto[];
  maquinarios: MaquinarioDto[];
  period: PeriodRange;
  onNovo: () => void;
  onEdit: (registro: RegistroHorimetroDto) => void;
  onDelete?: (registro: RegistroHorimetroDto) => void;
  onAdjustPeriod?: () => void;
}

function addDays(date: string, days: number): string {
  const result = new Date(`${date}T12:00:00`);
  result.setDate(result.getDate() + days);
  return result.toISOString().slice(0, 10);
}

function numberOfDays(startDate: string, endDate: string): number {
  const start = new Date(`${startDate}T12:00:00`).getTime();
  const end = new Date(`${endDate}T12:00:00`).getTime();
  return Math.round((end - start) / 86_400_000) + 1;
}

export function TabHorimetro({
  registros,
  maquinarios,
  period,
  onNovo,
  onEdit,
  onDelete,
  onAdjustPeriod,
}: Props) {
  const [selectedPeriod, setSelectedPeriod] = useState<PeriodRange>(period);
  const [fotosModal, setFotosModal] = useState<{
    titulo: string;
    fotos: FotoGaleriaItem[];
  } | null>(null);
  const [fotoPreview, setFotoPreview] = useState<FotoGaleriaItem | null>(null);
  const maquinaIds = useMemo(() => new Set(maquinarios.map((m) => m.id)), [maquinarios]);
  const hasValidPeriod = selectedPeriod.startDate <= selectedPeriod.endDate;

  const allRows = useMemo(() => {
    const scoped = registros.filter((r) => maquinaIds.has(r.maquinaId));
    return groupRegistrosPorDia(scoped);
  }, [registros, maquinaIds]);

  const rows = useMemo(
    () => (hasValidPeriod ? filterHorimetroDiaRowsByPeriod(allRows, selectedPeriod) : []),
    [allRows, hasValidPeriod, selectedPeriod],
  );

  const totalHoras = useMemo(() => {
    let total = 0;
    for (const m of maquinarios) {
      total += computeMaquinaHorasNoPeriodo(m.id, registros, selectedPeriod);
    }
    return total;
  }, [maquinarios, registros, selectedPeriod]);

  const previousPeriod = useMemo<PeriodRange | null>(() => {
    if (!hasValidPeriod) return null;
    const previousEnd = addDays(selectedPeriod.startDate, -1);
    if (previousEnd < period.startDate) return null;
    const previousStart = addDays(previousEnd, -(numberOfDays(selectedPeriod.startDate, selectedPeriod.endDate) - 1));
    return { startDate: previousStart < period.startDate ? period.startDate : previousStart, endDate: previousEnd };
  }, [hasValidPeriod, period.startDate, selectedPeriod]);

  const previousHoras = useMemo(() => {
    if (!previousPeriod) return null;
    return maquinarios.reduce(
      (total, maquina) => total + computeMaquinaHorasNoPeriodo(maquina.id, registros, previousPeriod),
      0,
    );
  }, [maquinarios, previousPeriod, registros]);

  const inicios = rows.filter((r) => r.inicio).length;
  const finais = rows.filter((r) => r.final).length;
  const abertas = rows.filter((r) => r.operacaoAberta).length;
  const foraDoPeriodo = useMemo(
    () => countRegistrosHorimetroForaDoPeriodo(registros, selectedPeriod, maquinaIds),
    [registros, selectedPeriod, maquinaIds],
  );
  const hourDifference = previousHoras == null ? null : totalHoras - previousHoras;

  const selectLastDays = (days: number) => {
    const startDate = addDays(period.endDate, -(days - 1));
    setSelectedPeriod({
      startDate: startDate < period.startDate ? period.startDate : startDate,
      endDate: period.endDate,
    });
  };

  return (
    <div className={locacoesTabContentClass}>
      <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="inline-flex items-center gap-2 text-sm font-semibold text-slate-700">
            <CalendarDays className="h-4 w-4 text-brand-600" aria-hidden />
            Filtro de tempo
          </div>
          <div className="flex items-center gap-2">
            <input
              type="date"
              value={selectedPeriod.startDate}
              min={period.startDate}
              max={selectedPeriod.endDate}
              onChange={(event) => setSelectedPeriod((current) => ({ ...current, startDate: event.target.value }))}
              className="h-9 rounded-lg border border-slate-200 bg-white px-2 text-sm text-slate-700"
              aria-label="Início do filtro de horímetros"
            />
            <span className="text-slate-400" aria-hidden>até</span>
            <input
              type="date"
              value={selectedPeriod.endDate}
              min={selectedPeriod.startDate}
              max={period.endDate}
              onChange={(event) => setSelectedPeriod((current) => ({ ...current, endDate: event.target.value }))}
              className="h-9 rounded-lg border border-slate-200 bg-white px-2 text-sm text-slate-700"
              aria-label="Fim do filtro de horímetros"
            />
          </div>
          <div className="flex flex-wrap gap-1">
            {[7, 15, 30].map((days) => (
              <Button key={days} type="button" size="sm" variant="ghost" onClick={() => selectLastDays(days)}>
                Últimos {days} dias
              </Button>
            ))}
            <Button type="button" size="sm" variant="ghost" onClick={() => setSelectedPeriod(period)}>
              Todo o contrato
            </Button>
          </div>
        </div>
        {!hasValidPeriod && <p className="mt-2 text-xs text-red-600">A data inicial deve ser anterior ou igual à data final.</p>}
      </div>
      {foraDoPeriodo > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950">
          <p className="inline-flex items-start gap-2">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" aria-hidden />
            <span>
              {foraDoPeriodo} registro(s) deste contrato estão fora do período selecionado (
              {formatPeriodLabel(selectedPeriod)}).
            </span>
          </p>
          {onAdjustPeriod && (
            <Button type="button" size="sm" variant="secondary" onClick={onAdjustPeriod}>
              Ajustar período do contrato
            </Button>
          )}
        </div>
      )}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <LocacoesStatCard
          title="Dias/sessões no período"
          value={rows.length}
          icon={<Clock className="h-5 w-5" />}
          accentClass="border-l-4 border-l-emerald-400"
        />
        <LocacoesStatCard title="Inícios" value={inicios} icon={<Clock className="h-5 w-5" />} accentClass="border-l-4 border-l-sky-400" />
        <LocacoesStatCard title="Finais" value={finais} icon={<Clock className="h-5 w-5" />} accentClass="border-l-4 border-l-purple-400" />
        <LocacoesStatCard
          title="Horas no período"
          value={formatHoras(totalHoras)}
          icon={<Clock className="h-5 w-5" />}
          accentClass="border-l-4 border-l-blue-400"
        />
      </div>

      {previousHoras != null && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-sky-100 bg-sky-50/60 px-4 py-3 text-sm">
          <div>
            <p className="font-semibold text-slate-800">Comparação com o período anterior</p>
            <p className="text-xs text-slate-600">
              {formatPeriodLabel(previousPeriod!)}: {formatHoras(previousHoras)}
            </p>
          </div>
          <div className="inline-flex items-center gap-1.5 font-semibold text-slate-800">
            {hourDifference! >= 0 ? (
              <TrendingUp className="h-4 w-4 text-emerald-600" aria-hidden />
            ) : (
              <TrendingDown className="h-4 w-4 text-red-600" aria-hidden />
            )}
            {hourDifference! >= 0 ? "+" : ""}
            {formatHoras(hourDifference!)} no período selecionado
          </div>
        </div>
      )}

      <div className="flex justify-end">
        <p className="mr-auto self-center text-xs text-slate-500 max-w-md">
          Cada linha agrupa Início e Final da mesma sessão. Horas trabalhadas são contabilizadas no dia civil do
          registro Final. Novo Início só é permitido após fechar a sessão com Final.
          {abertas > 0 ? ` ${abertas} operação(ões) aguardando final.` : ""}
        </p>
        <Button leftIcon={<Plus className="h-4 w-4" />} onClick={onNovo}>
          Novo registro de horímetro
        </Button>
      </div>

      <div className={locacoesTableWrapClass}>
        <table className="min-w-full text-sm">
          <thead className={locacoesTableHeadClass}>
            <tr>
              <th className="px-4 py-3">Máquina</th>
              <th className="px-4 py-3">Dia</th>
              <th className="px-4 py-3">Início (h)</th>
              <th className="px-4 py-3">Final (h)</th>
              <th className="px-4 py-3">Horas do dia</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Observação</th>
              <th className="px-4 py-3">Fotos</th>
              <th className="px-4 py-3 text-right">Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.length === 0 ? (
              <tr>
                <td colSpan={9} className="px-4 py-8 text-center text-slate-500">
                  Nenhum registro no período selecionado.
                </td>
              </tr>
            ) : (
              rows.map((row) => {
                const fotoCount = (row.inicio?.fotos?.length ?? 0) + (row.final?.fotos?.length ?? 0);
                const editTarget = row.final ?? row.inicio;
                return (
                  <tr key={row.id} className="hover:bg-slate-50/80">
                    <td className="px-4 py-3 font-medium text-slate-800">
                      {resolveMaquinaNome(maquinarios, row.maquinaId, row.maquinaNome)}
                    </td>
                    <td className="px-4 py-3">{formatDate(row.data)}</td>
                    <td className="px-4 py-3 tabular-nums">
                      {row.inicio ? `${row.inicio.horimetroInformado} h` : "—"}
                    </td>
                    <td className="px-4 py-3 tabular-nums">
                      {row.final ? `${row.final.horimetroInformado} h` : "—"}
                    </td>
                    <td className="px-4 py-3 tabular-nums font-medium text-emerald-700">
                      {row.horasTrabalhadas != null ? formatHoras(row.horasTrabalhadas) : "—"}
                    </td>
                    <td className="px-4 py-3">
                      {row.operacaoAberta ? (
                        <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800">
                          Aguardando final
                          {row.data < selectedPeriod.startDate || row.data > selectedPeriod.endDate ? " (fora do período)" : ""}
                        </span>
                      ) : (
                        <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-800">
                          Fechado
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 max-w-xs truncate text-slate-500">
                      {row.final?.observacao || row.inicio?.observacao || "—"}
                    </td>
                    <td className="px-4 py-3 text-slate-600">
                      {fotoCount > 0 ? (
                        <button
                          type="button"
                          className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 hover:underline"
                          onClick={() => {
                            const fotos = collectFotosDaLinha(row);
                            setFotosModal({
                              titulo: `${resolveMaquinaNome(maquinarios, row.maquinaId, row.maquinaNome)} · ${formatDate(row.data)}`,
                              fotos,
                            });
                          }}
                          aria-label={`Ver ${fotoCount} foto(s)`}
                        >
                          <Camera className="h-3.5 w-3.5" />
                          {fotoCount}
                        </button>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-1">
                        {editTarget && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => onEdit(editTarget)}
                            leftIcon={<Pencil className="h-3.5 w-3.5" />}
                          >
                            {fotoCount > 0 ? "Conferir" : "Editar"}
                          </Button>
                        )}
                        {onDelete && editTarget && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => onDelete(editTarget)}
                            leftIcon={<Trash2 className="h-3.5 w-3.5" />}
                          >
                            Excluir
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      {totalHoras > 0 && (
        <p className="text-xs text-slate-500">Total estimado no período: {formatHoras(totalHoras)}</p>
      )}

      <Modal
        open={Boolean(fotosModal)}
        onClose={() => {
          setFotosModal(null);
          setFotoPreview(null);
        }}
        title={fotosModal ? `Fotos · ${fotosModal.titulo}` : "Fotos"}
        size="lg"
        closeOnBackdropClick
        closeOnEscape
      >
        {fotosModal && (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {fotosModal.fotos.map((item) => (
              <button
                key={item.key}
                type="button"
                className="overflow-hidden rounded-lg border border-slate-200 bg-white text-left transition hover:border-emerald-300 hover:shadow-sm"
                onClick={() => setFotoPreview(item)}
              >
                <div className="aspect-[4/3] bg-slate-100">
                  <img
                    src={registroHorimetroFotoUrl(item.registroId, item.foto.id)}
                    alt={item.foto.originalName}
                    className="h-full w-full object-cover"
                  />
                </div>
                <div className="space-y-0.5 px-2 py-1.5">
                  <p className="text-[11px] font-medium uppercase tracking-wide text-emerald-700">{item.origem}</p>
                  <p className="truncate text-xs text-slate-600" title={item.foto.originalName}>
                    {item.foto.originalName}
                  </p>
                </div>
              </button>
            ))}
          </div>
        )}
      </Modal>

      {fotoPreview && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-4"
          onClick={() => setFotoPreview(null)}
          role="presentation"
        >
          <img
            src={registroHorimetroFotoUrl(fotoPreview.registroId, fotoPreview.foto.id)}
            alt={fotoPreview.foto.originalName}
            className="max-h-[90vh] max-w-[90vw] rounded-lg shadow-xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}
